// Header cart control: put the cart item count in its accessible name.
//
// Horizon names the control "Cart" (snippets/header-actions.liquid, the drawer
// <button> and the cart-page <a>) and points aria-describedby at #cart-bubble-text,
// whose only text is the aria-hidden bubble digit (snippets/cart-bubble.liquid). Hidden
// content is excluded from the description, so AT never got the count. This module
// keeps the control's aria-label at "Cart, N items" (plain "Cart" when the cart is
// empty) and drops the empty aria-describedby, so the count is spoken once, in the name.
//
// SOURCE OF TRUTH: the bubble assets/cart-icon.js keeps current. Every path that updates
// what a sighted user sees — an add, a quantity change, a removal, cart-icon's
// sessionStorage check, tcs-cart-history-sync.js's back-forward restore — updates the
// name too. Bubble hidden (`visually-hidden` on .cart-bubble, `hidden` on the count) is
// 0. At 100+ Horizon leaves the digit blank (render 'cart-bubble', limit: 100), so the
// count then comes from the last cart event or the server-rendered count.
//
// BACK-FORWARD RESTORE: a bfcache restore brings back this page's old last-known count,
// and tcs-cart-history-sync.js redraws the bubble from /cart.js without a cart event. At
// 100+ that redraw is blank, so the old count would be named ("Cart, 5 items" for a cart
// of 150). On a persisted pageshow the last-known count is dropped: a blank bubble is then
// named plain "Cart" until a cart event confirms a number. A failed /cart.js read changes
// nothing visible and still leaves no unconfirmed number in the name.
//
// RE-RENDERS (R-048 rule 3): nodes are re-queried on every sync, and the observer sits
// on #header-group, outside the header section, so a section re-render or a theme-editor
// reload that restores the server attributes is re-labelled.
//
// No live region: Horizon's header-actions status region and the cart drawer already
// announce updates (ledger A05). This changes only the control's name.
//
// Coupled to vendored markup: cart-icon, .cart-bubble, .cart-bubble__text-count, and the
// control being the closest button/link around cart-icon inside header-actions.
// scripts/tests/tcs-header-cart-count.test.mjs renders those real files.
import { StandardEvents } from '@shopify/events';

const loader = document.querySelector('script[data-tcs-header-cart-count]');
// All three strings come from the loader tag (locale keys); without them the stock
// markup is left exactly as Horizon rendered it.
const labels = {
  empty: loader?.dataset.labelEmpty || '',
  one: loader?.dataset.labelOne || '',
  other: loader?.dataset.labelOther || '',
};
const ready = Boolean(labels.empty && labels.one && labels.other);
const initial = Number.parseInt(loader?.dataset.itemCount ?? '', 10);
let lastKnownCount = Number.isInteger(initial) && initial >= 0 ? initial : null;

let pluralRules;
try {
  pluralRules = new Intl.PluralRules(document.documentElement.lang || 'en');
} catch {
  pluralRules = new Intl.PluralRules('en');
}

/** @param {Element} icon @returns {number | null} */
function readCount(icon) {
  const bubble = icon.querySelector('.cart-bubble');
  const count = icon.querySelector('.cart-bubble__text-count');
  if (!bubble || !count) return null;
  if (bubble.classList.contains('visually-hidden') || count.classList.contains('hidden')) return 0;
  const shown = Number.parseInt(count.textContent?.trim() ?? '', 10);
  if (Number.isInteger(shown) && shown >= 0) return shown;
  // Bubble visible but blank: Horizon's 100+ case.
  return lastKnownCount !== null && lastKnownCount > 0 ? lastKnownCount : null;
}

/** @param {number | null} count */
function labelFor(count) {
  if (!count) return labels.empty;
  const template = pluralRules.select(count) === 'one' ? labels.one : labels.other;
  return template.replace('[count]', String(count));
}

function sync() {
  if (!ready) return;
  for (const icon of document.querySelectorAll('header-actions cart-icon')) {
    const control = icon.closest('button, a');
    if (!control) continue;
    const label = labelFor(readCount(icon));
    if (control.getAttribute('aria-label') !== label) control.setAttribute('aria-label', label);
    // Its target is the aria-hidden digit: empty for AT, or a repeat of the count in an
    // engine that included it. The name now carries the count.
    if (control.getAttribute('aria-describedby') === 'cart-bubble-text') {
      control.removeAttribute('aria-describedby');
    }
  }
}

let queued = false;
function queueSync() {
  if (queued) return;
  queued = true;
  queueMicrotask(() => {
    queued = false;
    sync();
  });
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(({ cart, detail } = {}) => {
      const count = cart?.totalQuantity ?? detail?.itemCount;
      if (Number.isInteger(count) && count >= 0) lastKnownCount = count;
      queueSync();
    })
    .catch(() => {
      // cart-icon.js logs the rejection; the bubble, and so the name, stays as it was.
    });
});

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  lastKnownCount = null;
  queueSync();
});

new MutationObserver(queueSync).observe(document.getElementById('header-group') ?? document.body, {
  subtree: true,
  childList: true,
  characterData: true,
  attributes: true,
  attributeFilter: ['class', 'aria-label', 'aria-describedby'],
});

sync();
